// "Dispatch a load" guided flow (BUILD_SPEC.md §8.4 steps 1-3, §11). Plan.md G23
// — the primary sales-invoicing screen (HOME_TASKS routes "Dispatch a load"
// straight here). Four numbered steps ending in an explicit Post with a margin
// preview, per spec: pick customer (+ optional sales order) + product + from-
// location -> outbound weighbridge ticket -> price (defaulted from the customer's
// contract, editable) -> review & post. One truck/ticket produces ONE dispatch
// line (mirrors features/purchasing-intake/ReceiveLoadWizard.tsx's documented
// simplification for the inbound side: a weighbridge ticket carries a single
// gross/tare/product, so a multi-product load is entered as two separate runs).
import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Stepper,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconAlertCircle, IconCheck, IconTruck } from '@tabler/icons-react';
import { VAT_TREATMENTS, type VatTreatment } from '@zebra/core';
import { ApiRequestError } from '../../api/client';
import { useCanOperate, useCanSell } from '../../auth/permissions';
import { PageHeader } from '../../components/PageHeader';
import { CustomerSelect } from './components/CustomerSelect';
import { SalesOrderSelect } from './components/SalesOrderSelect';
import { ProductSelect } from './components/ProductSelect';
import { LocationSelect } from './components/LocationSelect';
import { SiteSelect } from './components/SiteSelect';
import { DownloadPdfLink } from './components/DownloadPdfLink';
import { DispatchMarginPreview, type MarginPreviewLine } from './components/DispatchMarginPreview';
import { centsToRand, formatMoneyCents, formatQty, nowForDatetimeLocal, randToCents, subtractKg, tonneCentsToUnitRand } from './format';
import {
  pickCurrentCustomerPrice,
  useCustomer,
  useCustomerPrices,
  useDispatchLoad,
  useProduct,
} from './api';

interface WizardState {
  siteId: string;
  customerId: string;
  salesOrderId: string;
  productId: string;
  productLabel: string;
  fromLocationId: string;
  locationLabel: string;
  vehicleReg: string;
  externalTicketNo: string;
  haulier: string;
  driver: string;
  grossKg: string;
  tareKg: string;
  weighedAt: string;
  priceRand: string;
  priceTouched: boolean;
  vatTreatment: VatTreatment;
  notes: string;
}

function initialState(): WizardState {
  return {
    siteId: '',
    customerId: '',
    salesOrderId: '',
    productId: '',
    productLabel: '',
    fromLocationId: '',
    locationLabel: '',
    vehicleReg: '',
    externalTicketNo: '',
    haulier: '',
    driver: '',
    grossKg: '',
    tareKg: '',
    weighedAt: nowForDatetimeLocal(),
    priceRand: '',
    priceTouched: false,
    vatTreatment: 'STANDARD',
    notes: '',
  };
}

const STEP_LABELS = ['Customer & product', 'Weighbridge ticket', 'Price', 'Review & post'];

export function DispatchWizard() {
  const canOperate = useCanOperate();
  const canSell = useCanSell();
  const canPostDispatch = canOperate || canSell;
  const [active, setActive] = useState(0);
  const [state, setState] = useState<WizardState>(initialState());

  const customer = useCustomer(state.customerId || undefined);
  const product = useProduct(state.productId || undefined);
  const customerPrices = useCustomerPrices({ customerId: state.customerId || undefined, productId: state.productId || undefined });
  const dispatchLoad = useDispatchLoad();

  function update<K extends keyof WizardState>(key: K, value: WizardState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  // Default the VAT treatment from the selected product's own vatTreatment, once
  // per product change — the user can still override it in step 2.
  useEffect(() => {
    if (product.data) setState((prev) => ({ ...prev, vatTreatment: product.data.vatTreatment }));
  }, [product.data]);

  // Prefill the editable price from the customer's contract (BUILD_SPEC.md §8.4
  // step 3: "Price defaulted from customer contract -> review -> editable"), only
  // while the user hasn't typed one in themselves.
  useEffect(() => {
    if (state.priceTouched) return;
    const current = pickCurrentCustomerPrice(customerPrices.data?.items);
    if (!current) return;
    if (current.priceCentsPerUnit != null) {
      setState((prev) => ({ ...prev, priceRand: centsToRand(current.priceCentsPerUnit) }));
    } else if (current.priceCentsPerTonne != null) {
      setState((prev) => ({ ...prev, priceRand: tonneCentsToUnitRand(current.priceCentsPerTonne as string) }));
    }
  }, [customerPrices.data, state.priceTouched]);

  function resetWizard() {
    setState(initialState());
    setActive(0);
    dispatchLoad.reset();
  }

  const netKg = subtractKg(state.grossKg || '0', state.tareKg || '0');
  const priceCentsPerUnit = randToCents(state.priceRand || '0');
  const isPerLoad = customer.data?.invoicingMode === 'PER_LOAD';

  const previewLines: MarginPreviewLine[] = state.productId
    ? [{ key: 'line-1', productId: state.productId, productLabel: state.productLabel, qty: netKg, priceCentsPerUnit }]
    : [];

  const canProceed = [
    Boolean(state.siteId && state.customerId && state.productId && state.fromLocationId),
    state.vehicleReg.trim() !== '' && state.grossKg !== '' && state.tareKg !== '' && Number(netKg) > 0 && state.weighedAt !== '',
    state.priceRand.trim() !== '' && Number(state.priceRand) >= 0,
    true,
  ];

  function post(alsoRaiseInvoice: boolean) {
    dispatchLoad.mutate({
      siteId: state.siteId,
      customerId: state.customerId,
      salesOrderId: state.salesOrderId || null,
      productId: state.productId,
      fromLocationId: state.fromLocationId,
      vehicleReg: state.vehicleReg.trim(),
      externalTicketNo: state.externalTicketNo.trim() || null,
      haulier: state.haulier.trim() || null,
      driver: state.driver.trim() || null,
      grossKg: state.grossKg,
      tareKg: state.tareKg,
      weighedAt: state.weighedAt,
      priceCentsPerUnit,
      vatTreatment: state.vatTreatment,
      notes: state.notes.trim() || null,
      alsoRaiseInvoice,
    });
  }

  if (dispatchLoad.isSuccess && dispatchLoad.data) {
    const { post: posted, raise } = dispatchLoad.data;
    const line = posted.movements[0];
    return (
      <Paper withBorder radius="md" p="xl" maw={640}>
        <Stack gap="md" align="flex-start">
          <Badge color="brandGreen" size="lg" leftSection={<IconCheck size={14} />}>
            Posted
          </Badge>
          <Title order={2}>Load dispatched</Title>
          <Text>{posted.summary}</Text>
          {line && (
            <Text size="sm" c="dimmed">
              {formatQty(line.qty)} kg dispatched — sale value {formatMoneyCents(line.saleValueCents)}, margin{' '}
              {formatMoneyCents(line.marginCents)}.
            </Text>
          )}
          <DownloadPdfLink
            label="Download delivery note"
            pdfKey={posted.deliveryNote?.key ?? null}
            downloadPath={`/sales/dispatches/${posted.dispatch.id}/delivery-note`}
          />
          {raise && (
            <Alert color={raise.invoices.length > 0 ? 'green' : 'amber'} icon={<IconCheck size={16} />} title="Invoice">
              {raise.summary}
            </Alert>
          )}
          <Group>
            {/* A bare `Link` (not Mantine's `component={Link}` polymorphism) so
                TanStack Router's `params` type is inferred correctly against the
                target route — mirrors features/silo-counts/components/
                StockCountsRegister.tsx's established pattern for this exact
                limitation. */}
            <Link to="/sales-invoicing/dispatches/$dispatchId" params={{ dispatchId: posted.dispatch.id }}>
              <Button component="span" variant="light">
                View dispatch
              </Button>
            </Link>
            <Button leftSection={<IconTruck size={16} />} onClick={resetWizard}>
              Dispatch another load
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader title="Dispatch a load" description="Dispatch a load over the weighbridge and post it out of stock." />
      <Stepper active={active} onStepClick={setActive} allowNextStepsSelect={false}>
        {STEP_LABELS.map((label) => (
          <Stepper.Step key={label} label={label} />
        ))}
      </Stepper>

      {active === 0 && (
        <Stack gap="sm" maw={520}>
          <SiteSelect
            label="Site"
            required
            value={state.siteId}
            onChange={(id) => setState((prev) => ({ ...prev, siteId: id, fromLocationId: '', locationLabel: '' }))}
          />
          <CustomerSelect
            label="Customer"
            required
            value={state.customerId}
            onChange={(id) => setState((prev) => ({ ...prev, customerId: id, salesOrderId: '' }))}
          />
          <SalesOrderSelect
            label="Sales order (optional)"
            description="Link this dispatch to an existing order so it counts against what the customer still has coming."
            customerId={state.customerId || undefined}
            value={state.salesOrderId}
            onChange={(id) => update('salesOrderId', id)}
          />
          <ProductSelect label="Product" required value={state.productId} onChange={(id, label) => setState((prev) => ({ ...prev, productId: id, productLabel: label }))} />
          <LocationSelect
            label="From silo/warehouse"
            required
            siteId={state.siteId || undefined}
            value={state.fromLocationId}
            onChange={(id, label) => setState((prev) => ({ ...prev, fromLocationId: id, locationLabel: label }))}
          />
        </Stack>
      )}

      {active === 1 && (
        <Stack gap="sm" maw={520}>
          <TextInput label="Vehicle registration" required value={state.vehicleReg} onChange={(e) => update('vehicleReg', e.currentTarget.value)} />
          <TextInput label="External ticket no. (optional)" value={state.externalTicketNo} onChange={(e) => update('externalTicketNo', e.currentTarget.value)} />
          <Group grow>
            <TextInput label="Haulier (optional)" value={state.haulier} onChange={(e) => update('haulier', e.currentTarget.value)} />
            <TextInput label="Driver (optional)" value={state.driver} onChange={(e) => update('driver', e.currentTarget.value)} />
          </Group>
          <Group grow>
            <TextInput label="Gross (kg)" required value={state.grossKg} onChange={(e) => update('grossKg', e.currentTarget.value)} />
            <TextInput label="Tare (kg)" required value={state.tareKg} onChange={(e) => update('tareKg', e.currentTarget.value)} />
          </Group>
          <Text size="sm" c="dimmed">
            Net: <strong>{formatQty(netKg)} kg</strong>
          </Text>
          <TextInput
            label="Weighed at"
            description="This is when the load left the yard — it drives which day's ledger and reports the dispatch falls under."
            type="datetime-local"
            required
            value={state.weighedAt}
            onChange={(e) => update('weighedAt', e.currentTarget.value)}
          />
          <Text size="sm" c="dimmed">
            No weighbridge ticket?{' '}
            <Text component={Link} to="/sales-invoicing/dispatch-manual" fw={600} span>
              Dispatch by count instead
            </Text>
          </Text>
        </Stack>
      )}

      {active === 2 && (
        <Stack gap="sm" maw={520}>
          {customerPrices.isFetching && (
            <Text size="sm" c="dimmed">
              Looking up the customer&apos;s contract price…
            </Text>
          )}
          <TextInput
            label="Price per unit (R, ex VAT)"
            required
            value={state.priceRand}
            onChange={(e) => setState((prev) => ({ ...prev, priceRand: e.currentTarget.value, priceTouched: true }))}
          />
          <Select
            label="VAT treatment"
            description="Whether VAT is added to this line — affects the invoice total once this dispatch is invoiced."
            data={VAT_TREATMENTS.map((v) => ({ value: v, label: v }))}
            value={state.vatTreatment}
            onChange={(v) => update('vatTreatment', (v as VatTreatment) ?? 'STANDARD')}
            comboboxProps={{ withinPortal: true }}
          />
          <Textarea label="Notes (optional)" autosize minRows={1} value={state.notes} onChange={(e) => update('notes', e.currentTarget.value)} />
        </Stack>
      )}

      {active === 3 && (
        <Stack gap="md" maw={640}>
          {dispatchLoad.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not post">
              {dispatchLoad.error instanceof ApiRequestError ? dispatchLoad.error.message : 'Failed to dispatch the load'}
            </Alert>
          )}
          <SimpleGrid cols={2} spacing="sm">
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">
                Customer
              </Text>
              <Text fw={600}>{customer.data ? `${customer.data.code} — ${customer.data.name}` : '—'}</Text>
            </Card>
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">
                Product
              </Text>
              <Text fw={600}>{state.productLabel || '—'}</Text>
            </Card>
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">
                Net weight
              </Text>
              <Text fw={600}>{formatQty(netKg)} kg</Text>
            </Card>
            <Card withBorder padding="sm">
              <Text size="xs" c="dimmed">
                From
              </Text>
              <Text fw={600}>{state.locationLabel || '—'}</Text>
            </Card>
          </SimpleGrid>

          <DispatchMarginPreview siteId={state.siteId || undefined} lines={previewLines} />

          {customer.data && (
            <Text size="sm" c="dimmed">
              Invoicing: <strong>{customer.data.invoicingMode}</strong>
              {isPerLoad ? ' — this load can be invoiced immediately after posting.' : ' — this load joins the uninvoiced queue for the next consolidated run.'}
            </Text>
          )}

          <Group justify="flex-end">
            {isPerLoad && (
              <Tooltip
                label={
                  canPostDispatch
                    ? 'Posts this dispatch and immediately raises the invoice — stock leaves the ledger, the document locks, and an invoice is created.'
                    : 'Requires Operations or Sales & Accounts role'
                }
              >
                <span>
                  <Button size="lg" variant="light" disabled={!canPostDispatch} loading={dispatchLoad.isPending} onClick={() => post(true)}>
                    Post &amp; raise invoice
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip
              label={
                canPostDispatch
                  ? 'Posts this dispatch — stock leaves the ledger and the document locks.'
                  : 'Requires Operations or Sales & Accounts role'
              }
            >
              <span>
                <Button size="lg" disabled={!canPostDispatch} loading={dispatchLoad.isPending} onClick={() => post(false)}>
                  Post
                </Button>
              </span>
            </Tooltip>
          </Group>
        </Stack>
      )}

      <Group justify="space-between">
        <Button variant="default" disabled={active === 0} onClick={() => setActive((a) => Math.max(0, a - 1))}>
          Back
        </Button>
        {active < 3 && (
          <Button disabled={!canProceed[active]} onClick={() => setActive((a) => Math.min(3, a + 1))}>
            Next
          </Button>
        )}
      </Group>
    </Stack>
  );
}
